/**
 * XML input validation for Player API
 *
 * Validates input data before XML request building to catch errors early.
 *
 * @example
 * validatePassengerConfig({
 *   adults: 2,
 *   children: [{ age: 8 }, { age: 12 }]
 * });
 */

import { ValidationError } from '@/types/errors';
import { PLAYER_API_CONFIG } from '@/constants/player/config';
import { $t } from '@/composables/translations/useTranslate';

const { LIMITS } = PLAYER_API_CONFIG;

/**
 * Passenger configuration for validation
 */
export interface PassengerConfig {
  adults: number;
  children: { age: number }[];
  /** Optional room assignments for multi-room bookings */
  rooms?: Array<{ passengerIds: number[] }>;
  /** Ordered passenger list. */
  passengers?: Array<{ age: number; type: 'adult' | 'child' }>;
}

/**
 * Period configuration for validation
 */
export interface PeriodConfig {
  start: string;
  end: string;
  duration: number;
  /**
   * Every length the gateway may return. Omitted (or empty) keeps today's
   * behaviour: a single `<Durations>` entry holding {@link PeriodConfig.duration}.
   */
  durations?: number[];
  /**
   * Lengths to rank first. Emitted as `<PreferredDurations>`; omitted or empty
   * emits no element at all. Values outside `durations` are ignored by the
   * gateway, so build the pair with `buildDurationPlan`.
   */
  preferredDurations?: number[];
}

/**
 * Validates passenger configuration
 *
 * Ensures passenger data meets API requirements before creating XML request.
 *
 * @throws ValidationError if validation fails
 *
 * @example
 * validatePassengerConfig({
 *   adults: 2,
 *   children: [{ age: 8 }]
 * });
 */
export function validatePassengerConfig(config: PassengerConfig): void {
  const errors: Array<{ field?: string; message: string }> = [];

  // Validate adults
  if (config.adults < 1) {
    errors.push({
      field: 'adults',
      message: $t('ibe.validation.passengers.atLeastOneAdult', 'At least 1 adult is required'),
    });
  }
  if (config.adults > LIMITS.MAX_ADULTS) {
    errors.push({
      field: 'adults',
      message: $t('ibe.validation.passengers.maxAdults', 'Maximum {count} adults allowed', {
        count: LIMITS.MAX_ADULTS,
      }),
    });
  }

  // Validate children
  if (config.children.length > LIMITS.MAX_CHILDREN) {
    errors.push({
      field: 'children',
      message: $t('ibe.validation.passengers.maxChildren', 'Maximum {count} children allowed', {
        count: LIMITS.MAX_CHILDREN,
      }),
    });
  }

  // Validate each child age
  config.children.forEach((child, index) => {
    if (child.age < LIMITS.MIN_CHILD_AGE || child.age > LIMITS.MAX_CHILD_AGE) {
      errors.push({
        field: `children[${index}].age`,
        message: $t(
          'ibe.validation.passengers.childAge',
          'Child age must be between {min} and {max}',
          {
            min: LIMITS.MIN_CHILD_AGE,
            max: LIMITS.MAX_CHILD_AGE,
          }
        ),
      });
    }
  });

  if (errors.length > 0) {
    throw new ValidationError(
      $t('ibe.validation.passengers.invalidConfiguration', 'Invalid passenger configuration'),
      errors
    );
  }
}

/**
 * Validates period configuration
 *
 * Ensures date range and duration are valid before creating XML request.
 *
 * @throws ValidationError if validation fails
 *
 * @example
 * validatePeriodConfig({
 *   start: '2025-11-15',
 *   end: '2025-11-28',
 *   duration: 7
 * });
 */
export function validatePeriodConfig(config: PeriodConfig): void {
  const errors: Array<{ field?: string; message: string }> = [];

  const start = new Date(config.start);
  const end = new Date(config.end);
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Reset time for date-only comparison

  // Validate dates are valid
  if (isNaN(start.getTime())) {
    errors.push({
      field: 'start',
      message: $t('ibe.validation.period.invalidStartDate', 'Invalid start date'),
    });
  }
  if (isNaN(end.getTime())) {
    errors.push({
      field: 'end',
      message: $t('ibe.validation.period.invalidEndDate', 'Invalid end date'),
    });
  }

  if (errors.length > 0) {
    throw new ValidationError(
      $t('ibe.validation.period.invalidConfiguration', 'Invalid period configuration'),
      errors
    );
  }

  // Validate start date is not in the past
  if (start < now) {
    errors.push({
      field: 'start',
      message: $t('ibe.validation.period.startDateInPast', 'Start date cannot be in the past'),
    });
  }

  // Validate end date is after start date
  if (end <= start) {
    errors.push({
      field: 'end',
      message: $t(
        'ibe.validation.period.endDateBeforeStartDate',
        'End date must be after start date'
      ),
    });
  }

  // Validate duration
  if (config.duration < 1) {
    errors.push({
      field: 'duration',
      message: $t('ibe.validation.period.minDuration', 'Duration must be at least 1 day'),
    });
  }
  if (config.duration > LIMITS.MAX_DURATION) {
    errors.push({
      field: 'duration',
      message: $t('ibe.validation.period.maxDuration', 'Maximum duration is {count} days', {
        count: LIMITS.MAX_DURATION,
      }),
    });
  }

  // Validate the optional multi-duration range. Only the new field is checked
  // here — the single-duration path below must not get any stricter than it is
  // today, or the no-op guarantee for `durationMode === 'exact'` breaks.
  if (config.durations !== undefined) {
    if (config.durations.length === 0) {
      errors.push({
        field: 'durations',
        message: $t('ibe.validation.period.emptyDurations', 'Duration range must not be empty'),
      });
    }
    if (config.durations.some((d) => !Number.isInteger(d) || d < 1)) {
      errors.push({
        field: 'durations',
        message: $t('ibe.validation.period.minDuration', 'Duration must be at least 1 day'),
      });
    }
  }

  // Validate duration fits within start/end range
  const daysBetween = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  if (config.durations !== undefined && config.durations.some((d) => d > daysBetween)) {
    errors.push({
      field: 'durations',
      message: $t(
        'ibe.validation.period.durationExceedsDateRange',
        'Duration {duration} days exceeds date range {dateRange} days',
        {
          duration: Math.max(...config.durations),
          dateRange: daysBetween,
        }
      ),
    });
  }
  if (config.duration > daysBetween) {
    errors.push({
      field: 'duration',
      message: $t(
        'ibe.validation.period.durationExceedsDateRange',
        'Duration {duration} days exceeds date range {dateRange} days',
        { duration: config.duration, dateRange: daysBetween }
      ),
    });
  }

  if (errors.length > 0) {
    throw new ValidationError(
      $t('ibe.validation.period.invalidConfiguration', 'Invalid period configuration'),
      errors
    );
  }
}
